import { FreeFieldBase } from '@models/DomainModels/Master/free-field-base';
import { FreeFieldEntityType } from '@models/enums/FreeFieldEntityType';
import { FreeFieldValueType } from '@models/enums/FreeFieldValueType';
import { ColDef, ValueGetterParams } from 'ag-grid-enterprise';

interface GridColumnParams {
  readonly t: (key: string, options?: { ns?: string }) => string;
}

const FILTER_DEBOUNCE_MS = 500;

/**
 * Creates a translated enum value formatter
 * @param enumObject - The enum object to process
 * @param translationPrefix - The translation key prefix
 * @param t - Translation function
 * @returns Formatted value or original if not found
 */
const createEnumValueFormatter =
  <T extends Record<string, string | number>>(enumObject: T, translationPrefix: string, t: (key: string, options?: { ns?: string }) => string) =>
  (params: { value?: string | number }): string => {
    if (!params.value) return '';

    const enumKey = Object.entries(enumObject).find(([, val]) => val === params.value)?.[0];
    return enumKey ? t(`${translationPrefix}.${enumKey}`) : String(params.value);
  };

/**
 * Creates filter value getter for enum fields
 * @param enumObject - The enum object to process
 * @param fieldName - The field name to extract from data
 * @param translationPrefix - The translation key prefix
 * @param t - Translation function
 * @returns Filter value or null
 */
const createEnumFilterValueGetter =
  <T extends Record<string, string | number>>(
    enumObject: T,
    fieldName: keyof FreeFieldBase,
    translationPrefix: string,
    t: (key: string, options?: { ns?: string }) => string
  ) =>
  (params: ValueGetterParams<FreeFieldBase>) => {
    const fieldValue = params.data?.[fieldName];
    if (!fieldValue) return null;

    const enumKey = Object.entries(enumObject).find(([, val]) => val === fieldValue)?.[0];
    return enumKey ? t(`${translationPrefix}.${enumKey}`) : String(fieldValue);
  };

/**
 * Column definitions for the FreeFields grid
 * @param t - Translation function
 * @returns Array of column definitions
 */
export const gridColumnDefinitions = ({ t }: GridColumnParams): ColDef<FreeFieldBase>[] => {
  const baseTextFilterParams = {
    caseSensitive: false as const,
    applyMiniFilterWhileTyping: true as const,
    debounceMs: FILTER_DEBOUNCE_MS
  };

  return [
    {
      field: 'name',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: baseTextFilterParams,
      headerName: t('headers.name'),
      sortable: true
    },
    {
      field: 'entityType',
      flex: 1,
      sortable: true,
      filter: 'agSetColumnFilter',
      headerName: t('headers.entityType'),
      valueFormatter: createEnumValueFormatter(FreeFieldEntityType, 'enums:freeFieldEntityType', t),
      filterValueGetter: createEnumFilterValueGetter(FreeFieldEntityType, 'entityType', 'enums:freeFieldEntityType', t)
    },
    {
      field: 'valueType',
      flex: 1,
      sortable: true,
      filter: 'agSetColumnFilter',
      headerName: t('headers.valueType'),
      valueFormatter: createEnumValueFormatter(FreeFieldValueType, 'enums:freeFieldValueType', t),
      filterValueGetter: createEnumFilterValueGetter(FreeFieldValueType, 'valueType', 'enums:freeFieldValueType', t)
    }
  ];
};
